import React, { useState, useEffect } from "react";
import { sbFetch } from "../utils/supabaseClient";
import { sendNotification } from "../utils/notifications";
import "../styles/CreateTab.css";

const NotificationsTab = ({ isCreateMode = false, isViewMode = false, onClose = null }) => {
  const [notifications, setNotifications] = useState([]);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [linkType, setLinkType] = useState("");
  const [linkId, setLinkId] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await sbFetch("GET", "notifications?order=sent_at.desc&limit=50");
      setNotifications(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading notifications:", err);
      setStatusMsg("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();

    if (!title.trim() || !message.trim()) {
      setStatusMsg("Title and message are required");
      return;
    }

    setSending(true);
    try {
      const success = await sendNotification(
        title.trim(),
        message.trim(),
        linkType || null,
        linkId || null
      );

      if (success) {
        setStatusMsg("Notification sent successfully!");
        setTitle("");
        setMessage("");
        setLinkType("");
        setLinkId("");
        // Reload notifications
        setTimeout(() => {
          loadNotifications();
          setStatusMsg("");
        }, 2000);
      } else {
        setStatusMsg("⚠️ Notification queued but may not have sent to all users");
      }
    } catch (err) {
      console.error("Error sending notification:", err);
      setStatusMsg("Failed to send notification");
    } finally {
      setSending(false);
    }
  };

  const handleDeleteNotification = async (id) => {
    if (!window.confirm("Delete this notification from the list?")) return;

    try {
      await sbFetch("DELETE", `notifications?id=eq.${id}`);
      setNotifications(notifications.filter((n) => n.id !== id));
      setStatusMsg("Notification deleted!");
      setTimeout(() => setStatusMsg(""), 3000);
    } catch (err) {
      console.error("Error deleting notification:", err);
      setStatusMsg("Failed to delete notification");
    }
  };

  const getLinkTypeLabel = (type) => {
    const labels = {
      learn: "📚 Learning",
      prompts: "💬 Prompts",
      resources: "🔗 Resources",
      assessment: "📋 Assessment",
      news: "📰 News",
    };
    return labels[type] || type || "—";
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: { bg: "rgba(59, 130, 246, 0.2)", color: "#5B9EF0", text: "Pending" },
      sent: { bg: "rgba(34, 197, 94, 0.2)", color: "#22C55E", text: "Sent" },
      failed: { bg: "rgba(248, 113, 113, 0.2)", color: "#F87171", text: "Failed" },
    };
    const style = colors[status] || colors.pending;
    return (
      <div
        style={{
          display: "inline-block",
          padding: "4px 10px",
          background: style.bg,
          color: style.color,
          borderRadius: "6px",
          fontSize: "11px",
          fontWeight: "600",
        }}
      >
        {style.text}
      </div>
    );
  };

  if (loading && (isViewMode || (!isCreateMode && !isViewMode))) {
    return <div className="loading">Loading notifications...</div>;
  }

  // Create mode - only show form
  if (isCreateMode) {
    return (
      <form onSubmit={handleSendNotification}>
        <div className="form-group">
          <label>Title *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., New Lesson Available"
            disabled={sending}
            style={{
              width: "100%",
              padding: "12px",
              background: "#050e1a",
              border: "1px solid rgba(59, 130, 246, 0.15)",
              borderRadius: "8px",
              color: "white",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
        </div>

        <div className="form-group">
          <label>Message *</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="The notification message users will see"
            rows="4"
            disabled={sending}
            style={{
              width: "100%",
              padding: "12px",
              background: "#050e1a",
              border: "1px solid rgba(59, 130, 246, 0.15)",
              borderRadius: "8px",
              color: "white",
              fontSize: "14px",
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
          />
        </div>

        <div style={{ display: "flex", gap: "16px" }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label>Link Type (optional)</label>
            <select
              value={linkType}
              onChange={(e) => setLinkType(e.target.value)}
              disabled={sending}
              style={{
                width: "100%",
                padding: "12px",
                background: "#050e1a",
                border: "1px solid rgba(59, 130, 246, 0.15)",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            >
              <option value="">— No Link —</option>
              <option value="learn">📚 Learning</option>
              <option value="prompts">💬 Prompts</option>
              <option value="resources">🔗 Resources</option>
              <option value="assessment">📋 Assessment</option>
              <option value="news">📰 News</option>
            </select>
          </div>

          <div className="form-group" style={{ flex: 1 }}>
            <label>Link ID (optional)</label>
            <input
              type="text"
              value={linkId}
              onChange={(e) => setLinkId(e.target.value)}
              placeholder="e.g., lesson_123456"
              disabled={sending}
              style={{
                width: "100%",
                padding: "12px",
                background: "#050e1a",
                border: "1px solid rgba(59, 130, 246, 0.15)",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            disabled={sending || !title.trim() || !message.trim()}
            className="btn-primary"
          >
            {sending ? "Sending..." : "📢 Send Notification"}
          </button>
        </div>

        {statusMsg && (
          <div
            className={`message ${
              statusMsg.includes("✅") ? "success" : statusMsg.includes("⚠️") ? "warning" : "error"
            }`}
          >
            {statusMsg}
          </div>
        )}
      </form>
    );
  }

  // View mode - only show list
  if (isViewMode) {
    return (
      <div>
        <h3 style={{ color: "#5B9EF0", marginBottom: "16px" }}>
          Recent Notifications ({notifications.length})
        </h3>

        <p style={{ color: "#7A9CC4", fontSize: "13px", marginBottom: "20px", lineHeight: "1.5" }}>
          Notifications are automatically added to this list when you send them using the "Create New" button.
          Each notification shows when it was sent, its status (Pending/Sent/Failed), and any associated links.
          You can delete notifications from this list if needed.
        </p>

        {notifications.length === 0 ? (
          <div
            style={{
              padding: "24px",
              textAlign: "center",
              color: "#7A9CC4",
              background: "rgba(59, 130, 246, 0.05)",
              borderRadius: "8px",
            }}
          >
            No notifications sent yet
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {notifications.map((notif) => (
              <div
                key={notif.id}
                style={{
                  padding: "14px",
                  background: "rgba(59, 130, 246, 0.08)",
                  border: "1px solid rgba(59, 130, 246, 0.2)",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                    gap: "12px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "600", color: "#5B9EF0", marginBottom: "4px" }}>
                      {notif.title}
                    </div>
                    <div style={{ fontSize: "13px", color: "#ccc", marginBottom: "6px" }}>
                      {notif.message}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "12px",
                        alignItems: "center",
                        fontSize: "11px",
                        color: "#7A9CC4",
                      }}
                    >
                      {notif.link_type && (
                        <span>{getLinkTypeLabel(notif.link_type)}</span>
                      )}
                      {notif.sent_at && (
                        <span>
                          {new Date(notif.sent_at).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    {getStatusBadge(notif.status)}
                    <button
                      onClick={() => handleDeleteNotification(notif.id)}
                      style={{
                        background: "rgba(248, 113, 113, 0.2)",
                        color: "#F87171",
                        border: "1px solid rgba(248, 113, 113, 0.4)",
                        borderRadius: "6px",
                        padding: "6px 10px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                      title="Delete notification"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Default mode - show both (backward compatibility)
  return (
    <div className="create-container">
      <h2>Notifications Manager</h2>

      <div style={{ width: "100%", maxWidth: "100%" }}>
        {/* Send Manual Notification */}
        <form onSubmit={handleSendNotification} style={{ marginBottom: "32px" }}>
          <h3 style={{ color: "#5B9EF0", marginBottom: "16px" }}>Send Manual Notification</h3>

          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., New Lesson Available"
              disabled={sending}
              style={{
                width: "100%",
                padding: "12px",
                background: "#050e1a",
                border: "1px solid rgba(59, 130, 246, 0.15)",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div className="form-group">
            <label>Message *</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="The notification message users will see"
              rows="4"
              disabled={sending}
              style={{
                width: "100%",
                padding: "12px",
                background: "#050e1a",
                border: "1px solid rgba(59, 130, 246, 0.15)",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                fontFamily: "inherit",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div style={{ display: "flex", gap: "16px" }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Link Type (optional)</label>
              <select
                value={linkType}
                onChange={(e) => setLinkType(e.target.value)}
                disabled={sending}
                style={{
                  width: "100%",
                  padding: "12px",
                  background: "#050e1a",
                  border: "1px solid rgba(59, 130, 246, 0.15)",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              >
                <option value="">— No Link —</option>
                <option value="learn">📚 Learning</option>
                <option value="prompts">💬 Prompts</option>
                <option value="resources">🔗 Resources</option>
                <option value="assessment">📋 Assessment</option>
                <option value="news">📰 News</option>
              </select>
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label>Link ID (optional)</label>
              <input
                type="text"
                value={linkId}
                onChange={(e) => setLinkId(e.target.value)}
                placeholder="e.g., lesson_123456"
                disabled={sending}
                style={{
                  width: "100%",
                  padding: "12px",
                  background: "#050e1a",
                  border: "1px solid rgba(59, 130, 246, 0.15)",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              disabled={sending || !title.trim() || !message.trim()}
              className="btn-primary"
            >
              {sending ? "Sending..." : "📢 Send Notification"}
            </button>
          </div>

          {statusMsg && (
            <div
              className={`message ${
                statusMsg.includes("✅") ? "success" : statusMsg.includes("⚠️") ? "warning" : "error"
              }`}
            >
              {statusMsg}
            </div>
          )}
        </form>

        {/* Notifications List */}
        <div>
          <h3 style={{ color: "#5B9EF0", marginBottom: "16px" }}>
            Recent Notifications ({notifications.length})
          </h3>

          {notifications.length === 0 ? (
            <div
              style={{
                padding: "24px",
                textAlign: "center",
                color: "#7A9CC4",
                background: "rgba(59, 130, 246, 0.05)",
                borderRadius: "8px",
              }}
            >
              No notifications sent yet
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {notifications.map((notif) => (
                <div
                  key={notif.id}
                  style={{
                    padding: "14px",
                    background: "rgba(59, 130, 246, 0.08)",
                    border: "1px solid rgba(59, 130, 246, 0.2)",
                    borderRadius: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      marginBottom: "8px",
                      gap: "12px",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: "600", color: "#5B9EF0", marginBottom: "4px" }}>
                        {notif.title}
                      </div>
                      <div style={{ fontSize: "13px", color: "#ccc", marginBottom: "6px" }}>
                        {notif.message}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          alignItems: "center",
                          fontSize: "11px",
                          color: "#7A9CC4",
                        }}
                      >
                        {notif.link_type && (
                          <span>{getLinkTypeLabel(notif.link_type)}</span>
                        )}
                        {notif.sent_at && (
                          <span>
                            {new Date(notif.sent_at).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      {getStatusBadge(notif.status)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Box */}
        <div
          style={{
            marginTop: "32px",
            padding: "16px",
            background: "rgba(59, 130, 246, 0.1)",
            border: "1px solid rgba(59, 130, 246, 0.3)",
            borderRadius: "8px",
            fontSize: "13px",
            color: "#7A9CC4",
            lineHeight: "1.6",
          }}
        >
          <strong>Notifications Note:</strong>
          <ul style={{ margin: "8px 0 0 20px", paddingLeft: "0" }}>
            <li>Notifications are sent to all app users immediately</li>
            <li>Link Type and Link ID are optional (for deep linking)</li>
            <li>Status shows pending, sent, or failed</li>
            <li>Recent notifications are listed below</li>
            <li>Notifications are stored with timestamps for audit trail</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default NotificationsTab;
